import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { AlertTriangle } from "lucide-react";

/**
 * In-design confirmation modal.
 *
 * Props:
 *  - open, onOpenChange : controlled state
 *  - title, description : strings
 *  - confirmLabel, cancelLabel : strings
 *  - tone: "danger" | "neutral"
 *  - onConfirm : async fn — when it resolves, the dialog closes
 *  - testIdPrefix : used to data-testid the buttons
 */
export default function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirmer",
  cancelLabel = "Annuler",
  tone = "danger",
  onConfirm,
  testIdPrefix = "confirm",
}) {
  const [busy, setBusy] = React.useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm?.();
      onOpenChange?.(false);
    } finally {
      setBusy(false);
    }
  };

  const confirmCls =
    tone === "danger"
      ? "bg-red-600 text-white hover:bg-red-700"
      : "bg-forest text-cream-50 hover:bg-forest-dark";

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-[60] bg-ink/40 backdrop-blur-sm data-[state=open]:animate-fade-in"
          data-testid={`${testIdPrefix}-overlay`}
        />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-[70] w-[92vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl bg-cream-50 shadow-lift border border-cream-300 p-7 sm:p-8 data-[state=open]:animate-fade-up"
          data-testid={`${testIdPrefix}-dialog`}
        >
          <div className="flex items-start gap-4">
            <div
              className={`w-11 h-11 rounded-2xl grid place-items-center shrink-0 ${
                tone === "danger" ? "bg-red-50 text-red-700" : "bg-forest-light text-forest"
              }`}
              aria-hidden="true"
            >
              <AlertTriangle size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <Dialog.Title className="font-heading text-xl text-ink font-semibold leading-tight">
                {title}
              </Dialog.Title>
              {description && (
                <Dialog.Description className="mt-2 text-sm text-ink-muted leading-relaxed">
                  {description}
                </Dialog.Description>
              )}
            </div>
          </div>

          <div className="mt-7 flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5">
            <Dialog.Close asChild>
              <button
                type="button"
                disabled={busy}
                className="inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-medium border border-cream-300 text-ink hover:bg-cream-100 transition disabled:opacity-50"
                data-testid={`${testIdPrefix}-cancel`}
              >
                {cancelLabel}
              </button>
            </Dialog.Close>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={busy}
              className={`inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-medium transition disabled:opacity-50 ${confirmCls}`}
              data-testid={`${testIdPrefix}-confirm`}
            >
              {busy ? "…" : confirmLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
